/**
 * Host-detection — M3a Signal A + Signal B per schema/host_probe.md.
 *
 *   Signal A: window.__vibeDesignerHost present (set by vibe-designer App
 *             at SPA boot — synchronous check).
 *   Signal B: GET /api/host-capabilities returns 200 with a well-formed
 *             body within a 2s budget (public route — no JWT needed).
 *
 * BOTH must agree to confirm module-host mode; either being absent puts
 * the SPA into standalone mode with graceful degradation. The contract
 * is intentionally fail-closed: any uncertainty → standalone.
 */
import type { HostMode } from './types';

const HOST_CAPABILITIES_PATH = '/api/host-capabilities';
// D-polish v1.2.3 (2026-06-02): bumped from 2000ms because users hit
// transient timeouts on first-load when vibe-designer's chrome is also
// initialising. Once services lock to 'standalone' the SPA has no way
// to recover without a hard reload (the picker would render the
// standalone placeholder permanently). The picker's retry button is
// the in-place recovery; this bump reduces how often that retry is
// needed at all.
const SIGNAL_B_TIMEOUT_MS = 5000;

export interface DetectHostOptions {
  fetchFn?: typeof fetch;
  timeoutMs?: number;
  capabilitiesPath?: string;
}

function hasSignalA(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.__vibeDesignerHost !== undefined;
}

function buildAbortSignal(timeoutMs: number): AbortSignal {
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
    return AbortSignal.timeout(timeoutMs);
  }
  const controller = new AbortController();
  setTimeout(() => controller.abort(), timeoutMs);
  return controller.signal;
}

interface CapabilitiesResponse {
  schema_version?: unknown;
  capabilities?: unknown;
}

function isWellFormed(body: unknown): body is CapabilitiesResponse {
  if (body === null || typeof body !== 'object') {
    return false;
  }
  const obj = body as CapabilitiesResponse;
  return Array.isArray(obj.capabilities);
}

export async function detectHost(options: DetectHostOptions = {}): Promise<HostMode> {
  if (!hasSignalA()) {
    return 'standalone';
  }
  const fetchFn = options.fetchFn ?? fetch;
  const path = options.capabilitiesPath ?? HOST_CAPABILITIES_PATH;
  const timeoutMs = options.timeoutMs ?? SIGNAL_B_TIMEOUT_MS;
  try {
    const resp = await fetchFn(path, { signal: buildAbortSignal(timeoutMs) });
    if (!resp.ok) {
      return 'standalone';
    }
    const body = (await resp.json()) as unknown;
    if (!isWellFormed(body)) {
      return 'standalone';
    }
    return 'module';
  } catch {
    return 'standalone';
  }
}
